import React, { FormEvent } from 'react';
import {
  AutoCompleteAiStickyBar,
  Error,
  FormActions,
  FormResponseProgress,
  FormSubmit,
  FormWrapper,
  QuestionResponses,
  RightSideSection,
} from './styles';
import AutoCompleteAiBtn from '../AutoCompleteAiBtn/AutoCompleteAiBtn';
import messages from './Messages';
import Icon from 'components/Icon';
import Input from 'components/Input';
import Button from 'components/Button';
import FileQuestion from '../FileQuestion';
import GridQuestion from '../GridQuestion';
import TextQuestion from '../TextQuestion';
import IndicatorQuestion from '../IndicatorQuestion';
import MultiChoiceQuestion from '../MultiChoiceQuestion';
import notify from 'components/Notify';
import { conditionMatchesAnswer } from './QuestionUtils';
import MultipleAnswerQuestion from '../MultipleAnswerQuestion';
import Collapsible from 'components/Collapsible';
import { MyBadge } from '../../FileManager/ItemsViewer/Survey/SurveyResponseViewer/styles';
import { convertNumbersToProperFormat, isANumber } from '../../../utils/number';
import { notifyError } from '../../Notify/Notify';
import axios from 'axios';
import FullScreenChoiceSelect from '../FullScreenChoiceSelect';
import { removeDuplicates, shuffleArray } from '../../../utils/happyFunctions';
import { ITags } from 'store/providers/providerTypes';
import {
  IAnswersView,
  IAnswerView,
  IGridAnswerView,
  IIndicatorAnswerView,
  IMultipleChoiceAnswerView,
  IMultipleChoiceQuestionView,
  IQuestionsView,
  ISubAnswer,
  ISurveyItemWithData,
  ITextAnswerView,
  ITextQuestionView,
  PeriodType,
  QuestionTypes,
  SurveyNotificationMessage,
} from 'store/survey/surveyTypes';
import { ISurveyResponseView, ISurveyView } from 'store/survey/surveyTypes';
import { IUsers } from 'store/store';
import { ICompany } from 'store/companies/companiesTypes';
import i18n from 'i18next';
import { getPropByPath } from '../../../store/objectHelpers';
import moment from 'moment-jalaali';
import uuid from 'uuid';
import ProgressBar from '../../ProgressBar/ProgressBar';
import { green } from '../../../utils/style/colors';
import { summerizeText } from '../../../utils/summerizeText';
import SurveyQuestionAnswerModal from './SurveyQuestionAnswerModal';
import { buildNewsComplementaryContext } from '../TitleInput/buildNewsComplementaryContext';
import handleQuestionFromChatBot from 'api/chatbot-api/AskQuestion';
import { DEFAULT_AI_GPT_MODEL } from 'api/chatbot-api/aiModels';
import { getPureTextFromHtml } from 'store/tasks/taskManipulate';

const DEFAULT_SURVEY_SYSTEM_PROMPT =
  'You are a helpful, professional reporter assistant that can extract the news from the content and return it in a text format. Your response should be precise and follow all news regarding it. Note: Do not get back with question and try to generate whatever the user is asking for. Return your response as a JSON object where each key is the exact question title and the value is the generated answer.';

const extractJSONFromString = (str: string): Record<string, string> | null => {
  const tryParse = (text: string) => {
    try {
      const parsed = JSON.parse(text);
      return typeof parsed === 'object' && parsed !== null ? parsed : null;
    } catch {
      return null;
    }
  };

  const direct = tryParse(str.trim());
  if (direct) return direct;

  const codeBlockMatch = str.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (codeBlockMatch) {
    const parsed = tryParse(codeBlockMatch[1].trim());
    if (parsed) return parsed;
  }

  const objectMatch = str.match(/\{[\s\S]*\}/);
  if (objectMatch) {
    return tryParse(objectMatch[0]);
  }

  return null;
};

export const ONE_WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;

interface IComponentProps {
  askTitle: boolean;
  recommendRefill?: boolean;
  submitMessage: string;
  preventDraft?: boolean;
  tags?: ITags['data'];
  setViewState: (
    showResponseForm: boolean,
    showDoneSubmit: boolean,
    sentResponseId?: number
  ) => void;
  singleResponseOnly: boolean;
  isUnique: boolean;
  response?: ISurveyResponseView;
  questions: { [questoinId: number]: IQuestionsView };
  anonymous: boolean;
  disableForm: boolean;
  loadingSubmit?: boolean;
  periodType: PeriodType;
  survey: ISurveyView;
  item: ISurveyItemWithData;
  selectedPeriod?: number;
  updateAnalyse: () => void;
  lastPossibleSubmitTime?: number;
  userRoles?: number[];
  thisUserId?: number;
  isEditor: boolean;
  loadUsers: (userIds: number[]) => Promise<any>;
  users: IUsers['map'];
  onSaveResponse: (
    answerList: ISurveyResponseView['surveyAnswerList'],
    status: 'update' | 'create',
    draft: boolean,
    title?: string,
    dontSendNotification?: boolean,
    activeQuestions?: number[]
  ) => Promise<any>;
  onGetNotifications: (
    answerList: ISurveyResponseView['surveyAnswerList'],
    status: 'update' | 'create',
    draft: boolean,
    title?: string
  ) => Promise<any>;
  distinctiveValues?: { [key: string]: string };
  company?: ICompany;
  currentUserCompanyRoles?: number[];
  openNextItem: () => void;
  randomInput?: boolean;
  dataSourceValues?: { [key: string]: string };
  surveyResponseUploadedProgress?: Record<string, number>;
}

interface IComponentStates {
  title?: string;
  saving: boolean;
  answers: { [key: number]: IAnswerView; error?: any };
  addTitle: boolean;
  draft: boolean;
  dontSendNotification: boolean;
  loadingNotification: boolean;
  notifications: SurveyNotificationMessage[] | undefined;
  dataSourceInfo: {
    [key: string]: {
      loading?: boolean;
      selected?: Record<string, unknown>;
      list: Record<string, unknown>[];
      currentQueryFields?: string;
      warning?: string;
      titleFields?: string[];
    };
  };
  randomizeOrder?: { [key: number]: number };
  showDataSourceChoices?: string;
  newResponseActiveQuestions?: number[];
  AIResponseModal: boolean;
  AIResponseAnswers?: { [key: number]: IAnswerView; error?: any };
  generatingAICompletion: boolean;
  aiGptModel: string;
  aiOccuringTimer: number | null;
  aiCompletionDuration: number | null;
}

export const QuestionsFrom: {
  [name in QuestionTypes]: React.ComponentClass<any>;
} = {
  file: FileQuestion,
  grid: GridQuestion,
  text: TextQuestion,
  indicator: IndicatorQuestion,
  multipleChoice: MultiChoiceQuestion,
  multipleAnswer: MultipleAnswerQuestion,
};

export function checkINID(value: string) {
  if (value.length !== 10) {
    return false;
  }
  if (!value.match(/[0-9]+/)) return false;
  let res = 0;
  for (let i = 0; i < 9; i++) {
    res += (10 - i) * Number(value[i]);
  }
  res = res % 11;
  if (res < 2) return Number(value[9]) === res;
  else return Number(value[9]) === 11 - res;
}

function getRandom(length: number) {
  return Math.floor(
    Math.pow(10, length - 1) + Math.random() * 9 * Math.pow(10, length - 1)
  );
}

class ResponseForm extends React.Component<IComponentProps, IComponentStates> {
  timeout: any = undefined;
  aiCompletionTimerInterval: number | undefined = undefined;
  constructor(props: IComponentProps) {
    super(props);
    let randomizeOrder: { [key: number]: number } | undefined = undefined;
    if (props.survey.randomizeOrder && !props.item.data.reportToken) {
      randomizeOrder = {};
      Object.keys(props.questions)
        .sort(() => Math.random() - 0.5)
        .forEach((item, index) => {
          randomizeOrder![item] = index;
        });
      Array.from(Array(101).keys())
        .sort(() => Math.random() - 0.5)
        .forEach((item, index) => {
          randomizeOrder![item] = index;
        });
    }
    if (props.response) {
      const newState = props.response.surveyAnswerList.reduce(
        (tmp, answer) => {
          tmp[answer.questionId] = answer;
          return tmp;
        },
        {} as Record<number, IAnswerView>
      );
      const title = props.response.title!;
      this.state = {
        randomizeOrder,
        title,
        saving: false,
        answers: newState,
        addTitle: !title,
        draft: props.response.draft!,
        notifications: undefined,
        dontSendNotification: false,
        loadingNotification: false,
        dataSourceInfo: {},
        AIResponseModal: false,
        generatingAICompletion: false,
        aiGptModel: DEFAULT_AI_GPT_MODEL,
        aiOccuringTimer: null,
        aiCompletionDuration: null,
      };
    } else {
      this.state = {
        randomizeOrder,
        answers: Object.values(props.questions).reduce(
          (current, item) => {
            if (item.deleted || item.disabled) return current;
            if (item.type === 'multipleChoice' && item.readFromItemTag) {
              const selected = props.item.data.tags
                .filter(
                  (it) =>
                    item.tagChoices?.includes(+it.id!) ||
                    (item.parentTagChoices?.includes(it.parentId!) &&
                      !item.excludedTagChoices?.includes(+it.id))
                )
                .map((it) => it.id);
              current[item.id] = {
                type: item.type,
                questionId: item.id,
                selected: selected,
              } as IMultipleChoiceAnswerView;
            } else if (
              item.type === 'multipleChoice' &&
              item.defaultValue &&
              item.defaultValue.length
            ) {
              current[item.id] = {
                type: item.type,
                questionId: item.id,
                selected: isANumber(item.defaultValue[0])
                  ? item.defaultValue
                  : [],
                values: !isANumber(item.defaultValue[0])
                  ? item.defaultValue
                  : [],
              } as IMultipleChoiceAnswerView;
            } else if (
              !this.props.randomInput &&
              !this.props.item.data.changeDataToken &&
              item.fields?.find((it) => it.name === 'conf_dataSourceField')
            ) {
              let propByPath = getPropByPath(
                this.props.dataSourceValues,
                item.fields!.find((it) => it.name === 'conf_dataSourceField')!
                  .value
              ) as string;
              const dateConvert = item.fields?.find(
                (it) => it.name === 'conf_dataSourceConvertDateFormat'
              )?.value;
              if (dateConvert && propByPath) {
                propByPath =
                  moment(propByPath, dateConvert)?.valueOf().toString() ||
                  propByPath;
              }
              const mapConvert = item.fields?.find(
                (it) => it.name === 'conf_dataSourceConvertMap'
              )?.value;
              if (mapConvert && propByPath) {
                propByPath = JSON.parse(mapConvert)[propByPath];
              }
              const randomDataOnMissing = item.fields?.find(
                (it) => it.name === 'conf_randomDataOnMissing'
              )?.value;
              if (randomDataOnMissing && !propByPath) {
                propByPath = uuid.v4();
              }
              if (item.type === 'multipleChoice') {
                current[item.id] = {
                  type: item.type,
                  questionId: item.id,
                } as IMultipleChoiceAnswerView;
                if (item.useTags)
                  (current[item.id] as IMultipleChoiceAnswerView).selected = [
                    Number(propByPath),
                  ];
                else
                  (current[item.id] as IMultipleChoiceAnswerView).values = [
                    propByPath,
                  ];
              } else
                current[item.id] = {
                  type: item.type,
                  questionId: item.id,
                  value: propByPath,
                } as ITextAnswerView;
            } else if (this.props.randomInput) {
              switch (item.type) {
                case 'text': {
                  let value = '';
                  switch (item.inputType) {
                    case 'text':
                      value = item.title + ' نمونه';
                      break;
                    case 'number':
                      value = getRandom(item.maxLength || 10).toString();
                      break;
                    case 'phone':
                      value = '09' + getRandom(9);
                      break;
                    case 'date':
                      value = Math.floor(new Date().getTime()).toString();
                      break;
                    case 'digits':
                      value = getRandom(item.maxLength || 10).toString();
                      break;
                    case 'area':
                      value = 'sample text';
                      break;
                    case 'inid':
                      value = '5926333865';
                      break;
                    case 'map':
                      value = '52,32';
                      break;
                    case 'rich':
                      value = '<h1>hi</h1>';
                      break;
                    case 'dateAndTime':
                      value = Math.floor(new Date().getTime()).toString();
                      break;
                    case 'duration':
                      value =
                        new Date().getHours() +
                        '.' +
                        Math.floor(
                          (new Date().getMinutes() * 100) / 60
                        ).toString();
                      break;
                  }
                  current[item.id] = {
                    type: item.type,
                    questionId: item.id,
                    value,
                  } as ITextAnswerView;
                  break;
                }
                case 'file':
                  break;
                case 'grid':
                  current[item.id] = {
                    type: item.type,
                    questionId: item.id,
                    subAnswers: item.parts.map((it) => {
                      return {
                        value:
                          item.choiceList[
                            Math.floor(Math.random() * item.choiceList.length)
                          ].value,
                        subQuestionValue: it.value,
                      } as ISubAnswer;
                    }),
                  } as IGridAnswerView;
                  break;
                case 'indicator': {
                  let numerator = 1;
                  let denominator = null as number | null;
                  if (item.denominatorUnit === item.numeratorUnit) {
                    denominator = Math.floor(Math.random() * 1000);
                    numerator =
                      Math.floor(Math.random() * 100000) % denominator;
                  }
                  if (item.denominatorUnit === null) {
                    numerator = Math.floor(Math.random() * 1000);
                  } else {
                    denominator = Math.floor(Math.random() * 1000);
                    numerator = Math.floor(Math.random() * 1000);
                  }
                  current[item.id] = {
                    type: item.type,
                    questionId: item.id,
                    numerator,
                    denominator,
                  } as IIndicatorAnswerView;
                  break;
                }
                case 'multipleAnswer':
                  break;
                case 'multipleChoice': {
                  let choices:
                    | IMultipleChoiceQuestionView['choiceList']
                    | Array<number> = [];
                  const question = item;
                  const tags = props.tags;
                  if (question.useTags && tags) {
                    // todo: parent tags could be null instead of array
                    if (question.parentTagChoices) {
                      question.parentTagChoices.forEach((parentId: number) => {
                        //todo should check for null
                        Object.values(tags)
                          .filter(
                            (tag: any) =>
                              tag.parentId === parentId &&
                              !tag.deleted &&
                              !question.excludedTagChoices?.includes(tag.id)
                          )
                          .sort(
                            (a, b) =>
                              Number(a.order || 0) - Number(b.order || 0)
                          )
                          .map((item) => {
                            // @ts-expect-error type change taking place
                            choices.push(item.id);
                          });
                      });
                    }
                    if (question.tagChoices) {
                      question.tagChoices.forEach((tagChoice: number) => {
                        choices.push(tagChoice as any);
                      });
                    }
                    // number[]
                    choices = removeDuplicates(choices as Array<number>).map(
                      (it) => {
                        return {
                          value: it,
                        } as any;
                      }
                    );
                  } else {
                    choices = question.choiceList;
                  }
                  const choice = (
                    choices as IMultipleChoiceQuestionView['choiceList']
                  )[Math.floor(Math.random() * choices.length)]?.value;
                  const values = item.useTags || !choice ? [] : [choice];
                  const selected =
                    item.useTags && choice ? [Number(choice)] : [];
                  current[item.id] = {
                    type: item.type,
                    questionId: item.id,
                    values,
                    selected,
                    value: choice,
                  } as IMultipleChoiceAnswerView;
                  break;
                }
              }
            }
            return current;
          },
          {} as { [key: number]: IAnswerView; error?: any }
        ),
        addTitle: true,
        saving: false,
        draft: false,
        dontSendNotification: false,
        notifications: undefined,
        loadingNotification: false,
        dataSourceInfo: {},
        AIResponseModal: false,
        generatingAICompletion: false,
        aiGptModel: DEFAULT_AI_GPT_MODEL,
        aiOccuringTimer: null,
        aiCompletionDuration: null,
      };
    }

    this.setAutoResponseTime();
  }

  private setAutoResponseTime = () => {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = undefined;
    }
    let finalPossibleSubmitDate = this.props.lastPossibleSubmitTime;
    if (
      this.props.survey.fillDurationInMinutes &&
      this.props.response?.submitDate
    ) {
      finalPossibleSubmitDate = Math.min(
        finalPossibleSubmitDate || Number.MAX_VALUE,
        this.props.response.submitDate +
          this.props.survey.fillDurationInMinutes * 60_000
      );
    }
    if (finalPossibleSubmitDate) {
      const now = new Date().getTime();
      if (now >= finalPossibleSubmitDate) {
        notify(i18n.t('noTimeToSend', { ns: 'survey' }), 'error', true, 5000);
        this.props.setViewState(false, false, undefined);
      } else if (finalPossibleSubmitDate - now < ONE_WEEK_IN_MS) {
        this.timeout = setTimeout(() => {
          this.onSubmitForm();
        }, finalPossibleSubmitDate - now);
      }
    }
  };

  componentWillUnmount() {
    if (this.timeout) clearTimeout(this.timeout);
    this.stopAiCompletionTimer();
  }

  titleInput = React.createRef<HTMLInputElement>();
  formTop = React.createRef<HTMLDivElement>();

  componentDidUpdate(_prevProps: IComponentProps, prevState: IComponentStates) {
    if (prevState.addTitle && !this.state.addTitle && this.titleInput.current) {
      this.titleInput.current.focus();
    }
    this.setAutoResponseTime();
  }

  componentDidMount() {
    let newResponseActiveQuestions = undefined as number[] | undefined;
    if (this.props.survey.autoSelectRandomQuestions) {
      newResponseActiveQuestions = shuffleArray(
        Object.values(this.props.survey.questionMap || {})
          .filter((it) => !it.data.deleted && !it.data.disabled)
          .map((it) => it.data.id)
      ).slice(0, this.props.survey.autoSelectRandomQuestions);
    }
    this.setState({ newResponseActiveQuestions });
    if (
      this.props.survey.surveyType === 'Exam' &&
      this.props.survey.fillDurationInMinutes &&
      !this.props.response?.id &&
      !this.props.item.data.changeDataToken
    ) {
      setTimeout(() => {
        this.props
          .onSaveResponse(
            [],
            'create',
            false,
            undefined,
            undefined,
            newResponseActiveQuestions
          )
          .catch(() => {
            /*do nothing*/
          });
      }, 500);
    }
  }

  fetchDataSource = (dataSourceName: string, force: boolean) => {
    const { survey } = this.props;
    const { answers, dataSourceInfo } = this.state;
    if (dataSourceInfo[dataSourceName]?.loading) {
      return;
    }
    const queryParams = {};
    const dataSourceText = survey.fields.find(
      (x: any) => x.name === `conf_dataSource_${dataSourceName}`
    )?.value;
    if (!dataSourceText) return;
    const dataSource = JSON.parse(dataSourceText);
    if (!dataSource.address) return;
    Object.values(this.props.questions).forEach((question) => {
      const field = question.fields?.find(
        (it) => it.name === `conf_dataSource_${dataSourceName}_queryField`
      );
      if (field?.value) {
        let value: string | undefined = undefined;
        if (question.type === 'text') {
          value = (answers[question.id] as ITextAnswerView)?.value;
        }
        if (question.type === 'multipleChoice') {
          if (question.useTags) {
            const selected = (answers[question.id] as IMultipleChoiceAnswerView)
              ?.selected[0];
            // @ts-expect-error it's ok
            value = this.props.tags?.[selected].name;
          } else {
            value = question.choiceList.find(
              (it) =>
                it.value ===
                  (answers[question.id] as IMultipleChoiceAnswerView)?.value ||
                (
                  answers[question.id] as IMultipleChoiceAnswerView
                )?.values?.includes(it.value)
            )?.text;
          }
        }
        if (value) queryParams[field.value] = value;
      }
    });
    const JSONStringOfFields = JSON.stringify(queryParams);
    if (
      dataSourceInfo[dataSourceName]?.currentQueryFields ===
        JSONStringOfFields &&
      !force
    )
      return;
    if (!dataSourceInfo[dataSourceName])
      dataSourceInfo[dataSourceName] = { loading: true, list: [] };
    else dataSourceInfo[dataSourceName].loading = true;
    this.setState({ dataSourceInfo });
    axios
      .get(dataSource.address, {
        params: { ...queryParams, ...(dataSource.extraParams || {}) },
      })
      .then(
        (response) => {
          let { dataSourceInfo } = this.state;
          dataSourceInfo = { ...dataSourceInfo };
          dataSourceInfo[dataSourceName] = {
            loading: false,
            selected: dataSourceInfo[dataSourceName]?.selected,
            list: response.data || [],
            currentQueryFields: JSONStringOfFields,
            titleFields: dataSource.titleFields,
          };
          this.setState({ dataSourceInfo });
        },
        () => {
          notify(i18n.t('fetchDataError', { ns: 'survey' }), 'error');
          dataSourceInfo[dataSourceName]!.warning = i18n.t('fetchDataError', {
            ns: 'survey',
          });
          dataSourceInfo[dataSourceName]!.loading = false;
        }
      );
  };

  onShowDataSourceModal = (dataSource: string) => {
    // if (!this.state.dataSourceInfo[dataSource]?.loading)
    this.fetchDataSource(dataSource, true);
    this.setState({ showDataSourceChoices: dataSource });
  };

  onSelectDataSourceItem = (
    sourceName: string,
    selectedObj: Record<string, unknown>
  ) => {
    const { dataSourceInfo, answers } = this.state;
    dataSourceInfo[sourceName].selected = selectedObj;
    this.setState({ dataSourceInfo });
    const toUpdateAnswers = {};
    Object.values(this.props.questions).forEach((question) => {
      if (
        question.fields?.find((it) => it.name === 'conf_dataSource')?.value !==
        sourceName
      )
        return;
      const field = question.fields?.find(
        (it) => it.name === 'conf_dataSource_field'
      );
      if (field) {
        const propName = field?.value;
        if (!propName) return;
        const value = selectedObj[propName];
        if (question.type === 'text') {
          toUpdateAnswers[question.id] = {
            type: 'text',
            value: value || (answers[question.id] as ITextAnswerView)?.value,
          };
        }
      }
    });
    if (Object.keys(toUpdateAnswers).length) {
      this.setState({ answers: { ...answers, ...toUpdateAnswers } });
    }
  };

  onSubmitForm = (e?: FormEvent) => {
    e?.preventDefault();
    const {
      setViewState,
      response,
      questions,
      updateAnalyse,
      onSaveResponse,
      periodType,
      singleResponseOnly,
      survey,
      item,
    } = this.props;
    const { answers, title, dontSendNotification } = this.state;

    let hasError = false;
    const filterdQuestionsData = getFilterdQuestions(
      questions,
      this.state.answers,
      survey,
      response,
      this.state.newResponseActiveQuestions
    );
    const invalidAnswers: string[] | undefined = this.state.draft
      ? undefined
      : filterdQuestionsData.reduce((tmp: any, q) => {
          if (q.type === 'multipleChoice' && q.answerRequired) {
            const qId = q.id;
            const answer = answers[qId] as IMultipleChoiceAnswerView;
            if (
              !answer ||
              (answer.value === null &&
                answer.values === null &&
                answer.selected === null)
            ) {
              const title = q.title;
              tmp.push(title);
            }
          }
          if (q.type === 'text' && q.answerRequired) {
            const qId = q.id;
            const answer = answers[qId] as ITextAnswerView;
            if (!answer || answer.value === null) {
              const title = q.title;
              tmp.push(title);
            }
          }
          return tmp;
        }, []);

    Object.values(answers).forEach((answer) => {
      if (answer.error) {
        hasError = true;
      }
    });
    if (hasError) {
      notify(i18n.t('pleaseFixWarnings', { ns: 'survey' }), 'warning');
    } else if (invalidAnswers && invalidAnswers.length > 0) {
      invalidAnswers.forEach((title) =>
        notify(messages.questionRequired(title), 'warning')
      );
    } else if (!this.props.selectedPeriod && periodType !== 'none') {
      notify(i18n.t('periodTimeRequired', { ns: 'survey' }), 'warning');
    } else {
      const answerList = this.getFinalAnswerList(answers, survey, response);
      const fillSurveyQuestionsViaAI =
        this.props.survey.fields?.find(
          (el) => el.name === 'conf_AIFillSurveyQuestion'
        )?.value || false;

      this.setState({ saving: true });
      if (fillSurveyQuestionsViaAI) {
        // appDispatch(fillingOutSurveyViaAIFn({})).then((res: any) => {
        //   const response = res.data;
        //   if (response) {
        //     this.setState({
        //       AIResponseModal: true,
        //       AIResponseAnswers: response,
        //     });
        //   }
        // });

        this.setState({
          AIResponseModal: true,
          AIResponseAnswers: {
            633985052693365: {
              type: 'text',
              questionId: 633985052693365,
              description: '',
              score: 0,
              scoreSum: null,
              scoreCount: null,
              value: 'needed 4 نمونهds',
            } as IAnswerView,
            109110529067605: {
              type: 'multipleAnswer',
              questionId: 109110529067605,
              description: '',
              score: 0,
              scoreSum: null,
              scoreCount: null,
              subAnswerList: [
                {
                  values: [
                    {
                      key: 'KhFJzrC5j7',
                      value: 'ds',
                    },
                  ],
                  implicit: false,
                  order: 1,
                  ref: 'TS34cHBYrn',
                  warning: null,
                },
              ],
            } as IAnswerView,
          },
        });
      } else {
        const type = response ? 'update' : 'create';
        onSaveResponse(
          answerList,
          type,
          this.state.draft,
          title,
          dontSendNotification,
          response ? undefined : this.state.newResponseActiveQuestions
        )
          .then((res) => {
            const { response } = res;
            if (type === 'update') {
              notify(i18n.t('responseUpdated', { ns: 'survey' }));
              updateAnalyse();
              setViewState(false, false);
              return;
            }
            const showDoneBox = Boolean(
              !item.data.changeDataToken
                ? singleResponseOnly ||
                    survey.fields.find(
                      (x: any) => x.name === 'conf_submitSuccessMessage'
                    )
                : false
            );
            const submitMessage = this.state.draft
              ? (i18n.t('responseSendAsDraft', { ns: 'survey' }) as string)
              : (
                  survey.fields.find(
                    (x: any) => x.name === 'conf_submitSuccessMessage'
                  ) || {
                    value: null,
                  }
                ).value || i18n.t('responseSend', { ns: 'survey' });

            const openNextOnFill =
              (
                this.props.survey.fields.find(
                  (x: any) => x.name === 'conf_openNextOnFill'
                ) || {
                  value: 'false ',
                }
              ).value === 'true';
            if (this.props.recommendRefill) {
              const r = window.confirm(
                i18n.t('refill', { ns: 'survey' }) as string
              );
              if (r) {
                notify(submitMessage as string);
                this.prepareForNewForm();
                return;
              }
            }
            if (openNextOnFill) {
              this.props.openNextItem();
            }
            setViewState(false, showDoneBox!, response?.data?.id ?? undefined);
            if (!showDoneBox) notify(submitMessage as string);
            updateAnalyse();
          })
          .catch((err: any) => {
            this.setState({ saving: false });
            notifyError(err);
          });
      }
    }
  };

  private getFinalAnswerList(
    answers: {
      [key: number]: IAnswerView;
      error?: any;
    },
    survey: ISurveyView,
    response?: ISurveyResponseView
  ) {
    const { questions } = this.props;
    const filterdQuestions = getFilterdQuestions(
      this.props.questions,
      this.state.answers,
      survey,
      response,
      this.state.newResponseActiveQuestions
    ).map((question) => {
      return question.id;
    });
    const answerList = Object.entries(answers).reduce(
      (list: ISurveyResponseView['surveyAnswerList'], answer: any) => {
        const [qId, response] = answer;
        const questionId = qId * 1;
        const question = questions[questionId];
        if (!question) return list;
        const answerView: IAnswersView = {
          ...response,
          questionId,
          type: question.type,
          selected:
            ((question.type === 'multipleChoice' &&
              question.useTags &&
              question.goDeepInTags) ??
            (0 > 0 && (question.showInTitle || question.distinctive)))
              ? response.selected?.reduce(
                  (acc: [], ans: string) => [
                    ...acc,
                    ...(typeof ans === 'string' ? ans : String(ans))?.split(
                      ','
                    ),
                  ],
                  []
                )
              : response.selected,
        };
        if (filterdQuestions.includes(questionId)) list.push(answerView);
        return list;
      },
      []
    );
    return answerList;
  }

  findNotification = () => {
    const { response, users, survey } = this.props;
    const { answers, title } = this.state;
    const type = response ? 'update' : 'create';
    const answerList = this.getFinalAnswerList(answers, survey, response);
    this.setState({ loadingNotification: true });
    this.props
      .onGetNotifications(answerList, type, this.state.draft, title)
      .then(
        ({ notifications }) => {
          const notLoadedUsers = notifications.reduce(
            (current: number[], item: any) => {
              if (!item.userId && !users[item.userId]) {
                current.push(item.userId);
              }
              return current;
            },
            [] as number[]
          );
          if (notLoadedUsers.length > 0) {
            this.props.loadUsers(notLoadedUsers).then(
              () => {
                this.setState({
                  loadingNotification: false,
                  notifications: notifications || [],
                });
              },
              () => {
                this.setState({
                  loadingNotification: false,
                  notifications: notifications || [],
                });
              }
            );
          } else
            this.setState({
              loadingNotification: false,
              notifications: notifications || [],
            });
        },
        (error) => {
          notifyError(error);
          this.setState({ loadingNotification: false });
        }
      );
  };

  prepareForNewForm = () => {
    const answers = Object.entries(this.state.answers).reduce(
      (list: { [key: number]: IAnswerView; error?: any }, answer: any) => {
        const [qId, response] = answer;
        const questionId = qId * 1;
        const fields = this.props.questions[questionId].fields;
        if (
          this.props.questions[questionId].distinctive ||
          (fields &&
            fields?.find((it: any) => it.name === 'conf_keepOnNextSubmit'))
        )
          list[qId] = response;
        return list;
      },
      {}
    );
    this.setState({ answers, title: undefined, saving: false });
    this.formTop.current!.scrollIntoView({ behavior: 'smooth' });
  };
  handleChangeAnswer = (answer: IAnswerView, questionId: number) => {
    this.setState((prevState) => ({
      answers: { ...prevState.answers, [questionId]: answer },
    }));
  };

  getNewsComplementaryContext = () => {
    return buildNewsComplementaryContext(
      this.props.questions,
      this.state.answers,
      this.props.tags
    );
  };

  autoCompleteViaAI = async () => {
    const complementaryContext = this.getNewsComplementaryContext();

    if (!complementaryContext) {
      notify(
        i18n.t('newsComplementaryAnswersRequired', { ns: 'survey' }) as string,
        'error'
      );
      return;
    }

    const newsQuestions = Object.values(this.props.questions).filter(
      (question) =>
        !question.deleted &&
        !question.disabled &&
        question.fields
          ?.find((field) => field.name === 'conf_newsQuestionToComplete')
          ?.value.toLowerCase() === 'true'
    );

    if (newsQuestions.length === 0) {
      return;
    }

    const updatedAnswers = { ...this.state.answers };

    const surveySystemPrompt =
      this.props.survey.fields?.find(
        (field) => field.name === 'conf_surveySystemPrompt'
      )?.value || DEFAULT_SURVEY_SYSTEM_PROMPT;

    const questionTitles = newsQuestions.map((question) =>
      getPureTextFromHtml(question.title)
    );

    const systemPrompt = [
      surveySystemPrompt,
      '',
      'Questions to complete:',
      ...questionTitles.map((title) => `- ${title}`),
    ].join('\n');

    const instruction = [
      {
        role: 'developer',
        content: [{ type: 'input_text', text: systemPrompt }],
      },
      {
        role: 'user',
        content: [{ type: 'input_text', text: complementaryContext }],
      },
    ];

    try {
      const { text: result, durationMs } = await handleQuestionFromChatBot(
        instruction,
        true,
        this.state.aiGptModel
      );
      if (!result) {
        notify(
          i18n.t('newsGenerationFailed', { ns: 'survey' }) as string,
          'error'
        );
        return;
      }

      const parsedAnswers = extractJSONFromString(result);
      if (!parsedAnswers) {
        notify(
          i18n.t('newsGenerationFailed', { ns: 'survey' }) as string,
          'error'
        );
        return;
      }

      const normalizedAnswers = Object.fromEntries(
        Object.entries(parsedAnswers).map(([key, value]) => [
          key.trim().toLowerCase(),
          String(value),
        ])
      );

      for (const question of newsQuestions) {
        const plainTitle = getPureTextFromHtml(question.title);
        if (!plainTitle) {
          continue;
        }

        const answerText =
          parsedAnswers[plainTitle] ??
          parsedAnswers[question.title] ??
          normalizedAnswers[plainTitle.trim().toLowerCase()];

        if (!answerText) {
          continue;
        }

        if (question.type === 'text') {
          const inputType = (question as ITextQuestionView).inputType;
          const value =
            inputType === 'rich' ? `<p>${answerText}</p>` : answerText;
          updatedAnswers[question.id] = {
            ...(updatedAnswers[question.id] as ITextAnswerView),
            type: 'text',
            questionId: question.id,
            value,
          } as ITextAnswerView;
        }
      }

      this.setState({
        answers: updatedAnswers,
        aiCompletionDuration: durationMs,
      });
      notify(i18n.t('newsGeneratedSuccessfully', { ns: 'survey' }) as string);
    } catch {
      notify(
        i18n.t('newsGenerationFailed', { ns: 'survey' }) as string,
        'error'
      );
    }
  };

  startAiCompletionTimer = () => {
    this.stopAiCompletionTimer();
    const startedAt = performance.now();
    this.setState({
      aiOccuringTimer: 0,
      aiCompletionDuration: null,
    });
    this.aiCompletionTimerInterval = window.setInterval(() => {
      this.setState({
        aiOccuringTimer: Math.round(performance.now() - startedAt),
      });
    }, 100);
  };

  stopAiCompletionTimer = () => {
    if (this.aiCompletionTimerInterval) {
      clearInterval(this.aiCompletionTimerInterval);
      this.aiCompletionTimerInterval = undefined;
    }
    this.setState({ aiOccuringTimer: null });
  };

  handleAiGptModelChange = (aiGptModel: string) => {
    this.setState({ aiGptModel });
  };

  handleAutoCompleteAi = async () => {
    this.setState({ generatingAICompletion: true });
    this.startAiCompletionTimer();
    try {
      await this.autoCompleteViaAI();
    } finally {
      this.stopAiCompletionTimer();
      this.setState({ generatingAICompletion: false });
    }
  };

  handleChangeTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ title: e.target.value });
  };

  handleChangeDraft = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ draft: e.target.checked });
  };

  handleDontSendMessage = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ dontSendNotification: e.target.checked });
  };

  addTitleHandler = () => {
    this.setState({ addTitle: false });
  };

  render() {
    const {
      // askTitle,
      tags,
      isUnique,
      setViewState,
      questions,
      disableForm,
      preventDraft,
      anonymous,
      loadingSubmit,
      distinctiveValues,
      response,
      singleResponseOnly,
      item,
      survey,
      users,
      userRoles,
      isEditor,
      company,
      currentUserCompanyRoles,
      thisUserId,
    } = this.props;

    const {
      // title,
      answers,
      draft,
      loadingNotification,
      notifications,
      dontSendNotification,
      randomizeOrder,
      AIResponseModal,
    } = this.state;

    let filteredQuestions = getFilterdQuestions(
      questions,
      this.state.answers,
      survey,
      response,
      this.state.newResponseActiveQuestions
    );

    if (randomizeOrder) {
      filteredQuestions = filteredQuestions.sort(
        (a, b) => randomizeOrder[a.id] - randomizeOrder[b.id]
      );
    }
    let hasError = false;
    Object.values(answers).forEach((answer) => {
      if (answer.error) {
        hasError = true;
      }
    });
    const dataSourceValues: { title: string; value: string }[] = [];
    if (this.state.showDataSourceChoices) {
      const dataSourceText = survey.fields.find(
        (x: any) =>
          x.name === `conf_dataSource_${this.state.showDataSourceChoices}`
      )?.value;
      if (dataSourceText) {
        const dataSourceTitleFields =
          this.state.dataSourceInfo[this.state.showDataSourceChoices]
            ?.titleFields || [];
        this.state.dataSourceInfo[this.state.showDataSourceChoices]?.list?.map(
          (it) => {
            let title = '';
            dataSourceTitleFields.forEach((fieldName) => {
              title =
                title + (title.length > 0 ? ' ' : '') + (it[fieldName] || '');
            });
            dataSourceValues.push({ title, value: JSON.stringify(it) });
          }
        );
      }
    }
    let stageQuestionComponent: any = undefined;
    const stageQuestion = survey.stageQuestion;
    const selectedDataSourceChoice =
      this.state.showDataSourceChoices &&
      this.state.dataSourceInfo[this.state.showDataSourceChoices]?.selected;

    if (AIResponseModal && this.state.AIResponseAnswers) {
      const activeQuestions = Object.values(questions).filter((el) =>
        el?.fields?.find((el) => el.name === 'conf_needToAddAnswer')
          ? true
          : false
      );

      return (
        <SurveyQuestionAnswerModal
          isOpen={AIResponseModal}
          onClose={() => {
            this.setState({ AIResponseModal: false });
          }}
          survey={survey}
          questions={activeQuestions}
          answers={this.state.AIResponseAnswers}
          tags={tags as any}
          company={company}
          surveyItem={item}
        />
      );
    }
    const customSubmitButtonTitle = survey.fields.find(
      (it) => it.name === 'conf_submitButtonTitle'
    )?.value;
    const showAutoCompleteAi = !!survey.fields?.find(
      (field) =>
        field.name === 'conf_AIFillOutOtherFieldsBeforeSubmit' &&
        field.value === 'true'
    );
    return (
      <form onSubmit={this.onSubmitForm} style={{ clear: 'both' }}>
        <div ref={this.formTop} />
        {this.state.showDataSourceChoices && (
          <FullScreenChoiceSelect
            choices={dataSourceValues}
            title={i18n.t('selectValueToFill', { ns: 'survey' }) as string}
            selected={
              selectedDataSourceChoice
                ? [JSON.stringify(selectedDataSourceChoice)]
                : []
            }
            maxCount={1}
            onSelect={(selected: string[]) => {
              if (selected.length) {
                this.onSelectDataSourceItem(
                  this.state.showDataSourceChoices!,
                  JSON.parse(selected[0])
                );
              }
              this.setState({ showDataSourceChoices: undefined });
            }}
            onDismiss={() => {
              this.setState({ showDataSourceChoices: undefined });
            }}
            loading={
              this.state.dataSourceInfo?.[this.state.showDataSourceChoices]
                ?.loading
            }
          />
        )}
        <div>
          <FormWrapper>
            <QuestionResponses
              disable={disableForm}
              hidden={
                (disableForm || loadingSubmit) && survey.surveyType === 'Exam'
              }
            >
              {filteredQuestions.map((question) => {
                const answer = this.state.answers[question.id];
                const hasDataSource = !!question.fields?.find(
                  (it) => it.name === 'conf_dataSourceField'
                );
                const hidden =
                  !this.props.item.data.reportToken &&
                  (question.fields?.find((it) => it.name === 'conf_hidden') ||
                    (question.fields?.find(
                      (it) => it.name === 'conf_dataSourceField'
                    ) &&
                      !answer?.value));
                // const protectViewFromThisUser =
                //   (question.viewableWith?.length ?? 0) > 0 &&
                //   !userRoles?.find((e) =>
                //     question.viewableWith?.includes('R' + e)
                //   );
                const protectEditFromThisUser =
                  (hasDataSource && !item.data.changeDataToken) ||
                  (question.protection && !item.data.changeDataToken) ||
                  ((question.editableWith?.length ?? 0) > 0 &&
                    !userRoles?.find((e) =>
                      question.editableWith?.includes('R' + e)
                    ) &&
                    !(
                      question.editableWith?.includes('@senderWhileSending') &&
                      (!response ||
                        (response.draft && response.userId === thisUserId))
                    ) &&
                    !(
                      question.editableWith?.includes('@senderWhileUpdating') &&
                      response &&
                      !response.draft &&
                      response.userId === thisUserId
                    ) &&
                    !isEditor);
                if (hidden || (protectEditFromThisUser && !answer)) {
                  return <></>;
                }
                const QuestionComponent =
                  QuestionsFrom[question.type as QuestionTypes];
                const questionComponent = (
                  <QuestionComponent
                    disabled={protectEditFromThisUser}
                    key={question.id}
                    tags={tags}
                    answer={answer}
                    isUnique={isUnique} // for hide distinctive questions
                    onChange={this.handleChangeAnswer}
                    question={question}
                    distinctiveValues={distinctiveValues}
                    isRequired={!draft && question.answerRequired}
                    canChangeData={!!item.data.changeDataToken}
                    company={company}
                    currentUserCompanyRoles={currentUserCompanyRoles}
                    isEditor={isEditor}
                    onShowDataSourceModal={this.onShowDataSourceModal}
                    randomizeOrder={randomizeOrder}
                    survey={survey}
                    dataSourceValues={this.props.dataSourceValues}
                    draft={draft}
                    answers={answers}
                    questions={this.props.questions}
                    getNewsComplementaryContext={
                      this.getNewsComplementaryContext
                    }
                  />
                );
                if (question.id === stageQuestion) {
                  stageQuestionComponent = questionComponent;
                  return <></>;
                }
                return questionComponent;
              })}
            </QuestionResponses>
            {this.state.saving &&
              this.props.surveyResponseUploadedProgress &&
              Object.entries(this.props.surveyResponseUploadedProgress).length >
                0 && (
                <FormResponseProgress>
                  {Object.entries(
                    this.props.surveyResponseUploadedProgress
                  ).map(([qid, percent]) => (
                    <div key={qid}>
                      <div style={{ flex: '1' }}>
                        {summerizeText(questions?.[Number(qid)]?.title, 8)}
                      </div>
                      <div style={{ width: '8rem' }}>
                        <ProgressBar
                          count={percent}
                          total={100}
                          color={green}
                        />
                      </div>
                      <div>{convertNumbersToProperFormat(percent)} %</div>
                    </div>
                  ))}
                </FormResponseProgress>
              )}
            {showAutoCompleteAi && (
              <AutoCompleteAiStickyBar>
                <AutoCompleteAiBtn
                  generating={this.state.generatingAICompletion}
                  onClick={this.handleAutoCompleteAi}
                  disabled={disableForm}
                  model={this.state.aiGptModel}
                  onModelChange={this.handleAiGptModelChange}
                  occuringTime={this.state.aiOccuringTimer}
                  completionDurationMs={this.state.aiCompletionDuration}
                />
              </AutoCompleteAiStickyBar>
            )}
            <FormSubmit>
              {hasError && (
                <Error>
                  {i18n.t('pleaseFixWarnings', { ns: 'survey' }) as string}
                </Error>
              )}
              {stageQuestionComponent}
              {item.data.changeDataToken && survey.notifications.length > 0 && (
                <Collapsible
                  defaultOpen={false}
                  headerBackgroundColor="var(--tertiary-gray)"
                  iconName="message-circle"
                  onOpen={this.findNotification}
                  header={
                    i18n.t('submitMyContainsNotification', {
                      ns: 'survey',
                    }) as string
                  }
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Button
                      loading={loadingNotification}
                      onClick={this.findNotification}
                    >
                      {i18n.t('checkNotifications', { ns: 'survey' }) as string}
                    </Button>
                    <Input
                      id="dontSendNotification"
                      type="checkbox"
                      label={
                        i18n.t('dontSendNotification', {
                          ns: 'survey',
                        }) as string
                      }
                      checked={dontSendNotification}
                      onChange={this.handleDontSendMessage}
                    />
                  </div>
                  {notifications !== undefined &&
                    (notifications.length ? (
                      notifications.map((notif, index) => (
                        <div
                          style={{
                            margin: '0.5rem',
                            display: 'flex',
                            alignItems: 'flex-start',
                          }}
                          key={index}
                        >
                          <MyBadge
                            size={12}
                            style={{
                              justifyContent: 'right',
                              flexShrink: 0,
                            }}
                          >
                            <Icon
                              type="user"
                              style={{ padding: '0 2px 0 5px' }}
                            />
                            <span
                              style={{
                                textAlign: 'right',
                                marginTop: '.2rem',
                              }}
                            >
                              {users[notif.userId]?.name}
                            </span>
                          </MyBadge>
                          <span>{notif.message}</span>
                        </div>
                      ))
                    ) : (
                      <li>
                        {
                          i18n.t('noMessageWillBeSent', {
                            ns: 'survey',
                          }) as string
                        }
                      </li>
                    ))}
                </Collapsible>
              )}

              <FormActions>
                <RightSideSection>
                  {item.data.changeDataToken ||
                  !this.props.lastPossibleSubmitTime ||
                  this.props.lastPossibleSubmitTime > new Date().getTime() ? (
                    <>
                      <Button
                        type="submit"
                        color="primary-color"
                        style={{ marginInlineEnd: '1em' }}
                        loading={this.state.saving || loadingSubmit}
                        disabled={disableForm || hasError}
                        // onClick={this.onSubmitForm}
                      >
                        <>
                          <Icon type="send" className="right" />
                          {customSubmitButtonTitle ||
                            (anonymous
                              ? i18n.t('submitAnonymously', { ns: 'survey' })
                              : response && !survey.fillDurationInMinutes
                                ? i18n.t('update', { ns: 'survey' })
                                : i18n.t('submit', { ns: 'survey' }))}
                        </>
                      </Button>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {response?.id &&
                          survey.surveyType === 'Exam' &&
                          !item.data.reportToken && (
                            <div>
                              {
                                i18n.t('autoSubmitOnTimeFinish', {
                                  ns: 'survey',
                                }) as string
                              }
                            </div>
                          )}
                        {!survey.fillDurationInMinutes &&
                          survey.surveyType === 'Exam' &&
                          !item.data.reportToken && (
                            <div>
                              {
                                i18n.t('editingNotAllowedAfterSend', {
                                  ns: 'survey',
                                }) as string
                              }
                            </div>
                          )}
                      </div>
                      {((!anonymous && !preventDraft) || draft) && (
                        <Input
                          id="draft"
                          type="checkbox"
                          label={i18n.t('draft', { ns: 'survey' }) as string}
                          checked={draft}
                          onChange={this.handleChangeDraft}
                          disabled={disableForm}
                        />
                      )}
                    </>
                  ) : (
                    <div>
                      {i18n.t('formExpired', { ns: 'survey' }) as string}
                    </div>
                  )}
                </RightSideSection>
                {!singleResponseOnly && (
                  <Button
                    color="red-outline"
                    onClick={() => setViewState(false, false)}
                  >
                    <Icon type="x" className="right" />
                    {i18n.t('cancel', { ns: 'survey' }) as string}
                  </Button>
                )}
              </FormActions>
            </FormSubmit>
          </FormWrapper>
        </div>
      </form>
    );
  }
}

export function getFilterdQuestions(
  questions: { [questoinId: number]: IQuestionsView },
  answers: { [key: number]: IAnswerView | string; error?: any },
  survey: ISurveyView,
  response?: ISurveyResponseView,
  activeQuestions?: number[],
  allowFollowup?: boolean
) {
  const sortedQuestions = Object.values(questions).sort(
    (a: any, b: any) => a.order - b.order
  );
  const hiddenQuestionIds = [] as number[];
  const filteredQuestions = [] as IQuestionsView[];
  const keepSubmitQuestionList =
    response && survey.versions[response.surveyVersion].keepSubmitQuestionList;
  for (let i = 0; i < sortedQuestions.length; i++) {
    const question = sortedQuestions[i];
    if (
      question.fields?.find((el) => el.name === 'conf_needToAddAnswer')
        ?.value === 'false' ||
      question.deleted ||
      (response && response.activeQuestions && keepSubmitQuestionList
        ? !response.activeQuestions?.includes(question.id)
        : question.disabled) ||
      (!response &&
        activeQuestions &&
        !activeQuestions.includes(question.id)) ||
      (!allowFollowup &&
        question.fields?.find((el) => el.name === 'conf_isFollowUp')?.value ===
          'true')
    )
      continue;
    const condition = question.viewCondition;
    const id = question.id;
    if (condition) {
      const refAnswer = answers[condition.refQuestionId] as
        | IMultipleChoiceAnswerView
        | string;
      const ret =
        refAnswer &&
        !hiddenQuestionIds.includes(+condition.refQuestionId) &&
        conditionMatchesAnswer(refAnswer, condition);
      if (!ret) {
        hiddenQuestionIds.push(+id);
        continue;
      }
    }
    filteredQuestions.push(question);
  }
  return filteredQuestions;
}

export function getTextFilterdQuestions(questions: {
  [questoinId: number]: IQuestionsView;
}) {
  const sortedQuestions = Object.values(questions).sort(
    (a: any, b: any) => a.order - b.order
  );

  const filteredQuestions = [] as IQuestionsView[];
  for (let i = 0; i < sortedQuestions.length; i++) {
    const question = sortedQuestions[i];
    if (question.deleted || question.disabled) continue;
    if (question.showInTitle && question.type === 'text')
      filteredQuestions.push(question);
  }
  return filteredQuestions;
}

export default ResponseForm;
